import React, { useMemo, useState } from "react";
import { Document, Page } from "react-pdf";
import StampBox from "../molecules/template/StampBox";
import PdfOverlayBox from "../molecules/template/PdfOverlayBox";
import {
  alternativeStampsColorMap,
  findPageForPosition,
  type PositionWithValueAndPage,
} from "../../utils/templateHelpers";
import type { Position } from "../../models/Position";
import type {
  PossibleFieldPositionWithValue,
  StampInformation,
} from "../../models/ClientInvoiceReaderTemplateModel";
import MaskOverlay from "../molecules/template/MaskOverlay";
import { useTranslation } from "react-i18next";

export interface CustomHighlight {
  id?: string;
  position: Position;
  pageNumber?: number;
  color?: string;
  background?: string;
  label?: string;
  skipRendering?: boolean; // For draw-mode masks that render as MaskOverlay instead
}

export interface StampPositionData {
  position: Position;
  alternativeStampPositions: Position[];
  templateLabel?: {
    value: string;
    position: Position;
  } | null;
}

export interface MaskOverlayData {
  id: string;
  position: Position;
  pageNumber?: number;
  showOnPages?: number[];
  isToggled?: boolean;
  showOnAllPages?: boolean;
  onChange: (pos: Position) => void;
}

interface Props {
  fileBase64: string;
  possibleFields: Array<PossibleFieldPositionWithValue>;
  displayHighlights: boolean;
  customHighlights?: CustomHighlight[];
  stampInformation?: StampInformation;
  onStampPositionChanged?: (pos: Position) => void;
  onAlternativeStampChanged?: (index: number, pos: Position) => void;
  stampsEditable?: boolean;
  maskOverlays?: MaskOverlayData[];
  activeMaskId?: string | null;
  onActiveMaskChange?: (id: string | null) => void;
  onHighlightResized?: (
    label: string,
    newPosition: Position,
    pageNumber: number,
  ) => void;
  onFieldClick?: (field: PossibleFieldPositionWithValue) => void;
  currentPage: number;
  onNumPagesChange?: (numPages: number) => void;
  scale?: number;
  maskOverlaysDraggable?: boolean;
}

const PdfTemplate: React.FC<Props> = ({
  fileBase64,
  possibleFields,
  displayHighlights,
  customHighlights,
  stampInformation,
  onStampPositionChanged,
  onAlternativeStampChanged,
  stampsEditable = true,
  maskOverlays,
  onActiveMaskChange,
  activeMaskId,
  onHighlightResized,
  onFieldClick,
  currentPage,
  onNumPagesChange,
  scale: scaleProp,
  maskOverlaysDraggable = true,
}) => {
  const [pageHeight, setPageHeight] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const [hoveredFieldIdx, setHoveredFieldIdx] = useState<number | null>(null);
  const scale = scaleProp ?? 1.2;
  const { t } = useTranslation();

  const pdfFile = useMemo(() => {
    const binary = atob(fileBase64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return { data: bytes };
  }, [fileBase64]);

  const convert = (pos: Position) => {
    const x = pos.bottomLeftX * scale;
    const y = pos.bottomLeftY * scale;

    const width = (pos.topRightX - pos.bottomLeftX) * scale;
    const height = (pos.topRightY - pos.bottomLeftY) * scale;

    const top = pageHeight - y - height;

    return { left: x, top, width, height };
  };

  const stampLabelPage = stampInformation?.label
    ? ((stampInformation.label as PositionWithValueAndPage).pageNumber ??
      findPageForPosition(stampInformation.label.position, possibleFields) ??
      1)
    : undefined;
  const showStampsOnPage =
    !stampInformation?.label || currentPage === stampLabelPage;

  return (
    <div
      style={{
        position: "relative",
        overflowX: "auto",
        overflowY: "hidden",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Document
        file={pdfFile}
        loading={<div>{t("random.loadingPdf")}</div>}
        error={<div>{t("random.failedToLoadPdf")}</div>}
        onLoadSuccess={({ numPages: n }) => {
          onNumPagesChange?.(n);
        }}
      >
        <Page
          pageNumber={currentPage}
          scale={scale}
          onLoadSuccess={(page) => {
            setPageHeight(page.height);
            setPageWidth(page.width);
          }}
        />
      </Document>

      {pageWidth > 0 && pageHeight > 0 && displayHighlights && (
        <>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: pageWidth,
              height: pageHeight,
              pointerEvents: "none",
            }}
          >
            {/* Custom highlights - static (no resize callback or no label) */}
            {customHighlights
              ?.filter((item) => !item.skipRendering)
              .filter((item) => !onHighlightResized || !item.label)
              .filter(
                (item) => !item.pageNumber || item.pageNumber === currentPage,
              )
              .map((item) => {
                const box = convert(item.position);
                return (
                  <div
                    key={item.id}
                    style={{
                      position: "absolute",
                      pointerEvents: "none",
                      ...box,
                      border: `1px solid ${item.color ?? "rgba(0,200,0,0.8)"}`,
                      background: item.background ?? "rgba(0,200,0,.15)",
                    }}
                  />
                );
              })}
          </div>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: pageWidth,
              height: pageHeight,
              pointerEvents: "auto",
            }}
          >
            {/* Custom highlights - resizable */}
            {onHighlightResized &&
              customHighlights
                ?.filter((item) => !item.skipRendering && !!item.label)
                .filter(
                  (item) => !item.pageNumber || item.pageNumber === currentPage,
                )
                .map((item) => (
                  <PdfOverlayBox
                    key={item.id}
                    defaultPosition={item.position}
                    pdfHeight={pageHeight}
                    scale={scale}
                    onChange={(newPos) =>
                      onHighlightResized(
                        item.label!,
                        newPos,
                        item.pageNumber ?? currentPage,
                      )
                    }
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        border: `1px solid ${item.color ?? "rgba(0,200,0,0.8)"}`,
                        background: item.background ?? "rgba(0,200,0,.15)",
                        boxSizing: "border-box",
                        cursor: "move",
                      }}
                    />
                  </PdfOverlayBox>
                ))}

            {/* Primary stamp */}
            {stampInformation &&
              stampInformation.position &&
              showStampsOnPage && (
                <StampBox
                  defaultPosition={stampInformation.position}
                  pdfHeight={pageHeight}
                  scale={scale}
                  stampInformation={stampInformation}
                  onChange={onStampPositionChanged}
                  isDraggable={stampsEditable}
                />
              )}

            {/* Alternative stamps */}
            {stampInformation &&
              showStampsOnPage &&
              stampInformation?.alternativeStampPositions.map((pos, idx) => (
                <StampBox
                  key={idx}
                  defaultPosition={pos}
                  pdfHeight={pageHeight}
                  scale={scale}
                  stampInformation={stampInformation}
                  borderColor={
                    alternativeStampsColorMap[
                      idx % alternativeStampsColorMap.length
                    ]
                  }
                  onChange={(newPos) =>
                    onAlternativeStampChanged?.(idx, newPos)
                  }
                  isDraggable={stampsEditable}
                />
              ))}
            {/* Draw-mode masks - filter by page */}
            {maskOverlays
              ?.filter(
                (mask) =>
                  mask.showOnAllPages ||
                  (mask.showOnPages
                    ? mask.showOnPages.includes(currentPage)
                    : mask.pageNumber != null
                      ? mask.pageNumber === currentPage
                      : currentPage === 1),
              )
              .map((mask) => (
                <MaskOverlay
                  key={mask.id}
                  position={mask.position}
                  pdfHeight={pageHeight}
                  scale={scale}
                  isActive={activeMaskId === mask.id}
                  isToggled={mask.isToggled}
                  onChange={mask.onChange}
                  onActivate={() => onActiveMaskChange?.(mask.id)}
                  isDraggable={maskOverlaysDraggable ? undefined : false}
                />
              ))}
          </div>
        </>
      )}

      {/* Possible values — rendered independently of displayHighlights so click-to-select always works */}
      {pageWidth > 0 &&
        pageHeight > 0 &&
        possibleFields
          .filter((p) => p.pageNumber === currentPage)
          .map((item, idx) => {
            if (!item.position) return null;
            const box = convert(item.position);
            const isHovered = hoveredFieldIdx === idx;
            return (
              <div
                key={idx}
                onClick={onFieldClick ? () => onFieldClick(item) : undefined}
                onMouseEnter={() => setHoveredFieldIdx(idx)}
                onMouseLeave={() => setHoveredFieldIdx(null)}
                style={{
                  position: "absolute",
                  zIndex: 3,
                  pointerEvents: onFieldClick ? "auto" : "none",
                  cursor: onFieldClick ? "pointer" : undefined,
                  ...box,
                  border:
                    isHovered && onFieldClick
                      ? "2px solid rgba(161,172,188,1)"
                      : "1px solid rgba(161,172,188,.8)",
                  background:
                    isHovered && onFieldClick
                      ? "rgba(161,172,188,.3)"
                      : "rgba(161,172,188,.12)",
                }}
              />
            );
          })}
    </div>
  );
};

export default PdfTemplate;
